import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';
import { PROJECTS } from './projects';
import ProjectDetail from './ProjectDetail';
import Background from './Background';

gsap.registerPlugin(ScrollTrigger);

const Chars = ({ text }) =>
  text.split('').map((c, i) => (
    <span className="char" key={i}>
      {c}
    </span>
  ));

const App = () => {
  const [active, setActive] = useState(null);
  const showcaseRef = useRef(null);
  const trackRef = useRef(null);
  const counterRef = useRef(null);
  const barRef = useRef(null);
  const year = new Date().getFullYear();
  const liveCount = PROJECTS.filter((p) => p.status === 'live').length;

  const handleClose = useCallback(() => setActive(null), []);

  useEffect(() => {
    document.body.style.overflow = active ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [active]);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = gsap.context(() => {
      if (!reduced) {
        gsap.fromTo(
          '.hero-title .char',
          { yPercent: 120, rotate: 8 },
          { yPercent: 0, rotate: 0, duration: 1.1, ease: 'power4.out', stagger: 0.04, delay: 0.15 }
        );
        gsap.fromTo(
          '.hero-sub, .hero-stats, .hero-scroll',
          { y: 26, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out', stagger: 0.1, delay: 0.75 }
        );
      }

      const mm = gsap.matchMedia();

      // desktop: vertical scroll drives a pinned horizontal pan through the apps
      mm.add('(min-width: 900px)', () => {
        const track = trackRef.current;
        const dist = () => track.scrollWidth - window.innerWidth;

        const scrollTween = gsap.to(track, {
          x: () => -dist(),
          ease: 'none',
          scrollTrigger: {
            trigger: showcaseRef.current,
            start: 'top top',
            end: () => '+=' + dist(),
            scrub: reduced ? false : 1,
            pin: true,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              const idx = 1 + Math.round(self.progress * (PROJECTS.length - 1));
              if (counterRef.current) counterRef.current.textContent = `0${idx}`;
              if (barRef.current) barRef.current.style.transform = `scaleX(${self.progress})`;
            },
          },
        });

        // micro-parallax: media drifts slower than its panel
        gsap.utils.toArray('.panel-media').forEach((media) => {
          gsap.fromTo(
            media,
            { x: 70 },
            {
              x: -70,
              ease: 'none',
              scrollTrigger: {
                trigger: media,
                containerAnimation: scrollTween,
                start: 'left right',
                end: 'right left',
                scrub: true,
              },
            }
          );
        });
      });

      // mobile: simple vertical stack with reveals
      mm.add('(max-width: 899px)', () => {
        if (reduced) return;
        gsap.utils.toArray('.panel').forEach((panel) => {
          gsap.fromTo(
            panel,
            { y: 56, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.9,
              ease: 'power3.out',
              scrollTrigger: { trigger: panel, start: 'top 88%' },
            }
          );
        });
      });
    });

    return () => ctx.revert();
  }, []);

  const openProject = (p) => setActive(p);

  return (
    <div className="page">
      <Background />

      <header className="topbar">
        <a className="wordmark" href="/">
          EXGO<sup>®</sup>
        </a>
        <nav className="topnav">
          <a href="#work">APPS</a>
          <a href="https://twitter.com/GirtsOzo" target="_blank" rel="noreferrer">
            𝕏
          </a>
          <a href="mailto:info@exgo.one">CONTACT</a>
        </nav>
      </header>

      <section className="hero">
        <h1 className="hero-title">
          <span className="line">
            <Chars text="GIRTS" />
          </span>
          <span className="line">
            <Chars text="OZOLS" />
          </span>
        </h1>
        <p className="hero-sub">IT professional — building SaaS &amp; mobile apps.</p>
        <p className="hero-stats">
          {PROJECTS.length} PROJECTS · {liveCount} LIVE
        </p>
        <p className="hero-scroll">SCROLL ↓</p>
      </section>

      <section className="showcase" id="work" ref={showcaseRef}>
        <div className="showcase-hud">
          <span className="show-counter">
            <span ref={counterRef}>01</span> / 0{PROJECTS.length}
          </span>
          <div className="show-bar">
            <div className="show-bar-fill" ref={barRef} />
          </div>
          <span className="show-hint">KEEP SCROLLING</span>
        </div>

        <div className="track" ref={trackRef}>
          {PROJECTS.map((p) => (
            <article
              key={p.id}
              className="panel"
              style={{ '--accent': p.accent }}
              role="button"
              tabIndex={0}
              aria-label={`Open ${p.title}`}
              onClick={() => openProject(p)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openProject(p)}
            >
              <span className="panel-num" aria-hidden>
                {p.index}
              </span>
              <div className="panel-media">
                <img src={p.gif} alt={`${p.title} preview`} loading="lazy" draggable="false" />
              </div>
              <div className="panel-info">
                <span className={`panel-status ${p.status}`}>
                  {p.status === 'live' ? '● LIVE' : 'ARCHIVED'}
                </span>
                <h3>{p.title}</h3>
                <p>{p.tagline}</p>
                <span className="panel-open">
                  OPEN <i>↗</i>
                </span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <footer className="footer">
        <span>© {year} EXGO</span>
        <a href="mailto:info@exgo.one">INFO@EXGO.ONE</a>
        <a href="https://twitter.com/GirtsOzo" target="_blank" rel="noreferrer">
          𝕏 GIRTSOZO
        </a>
      </footer>

      {active && <ProjectDetail project={active} onClose={handleClose} />}
    </div>
  );
};

export default App;
